import icons from '@/components/atoms/Icons'
import { Bubble, Button } from '@dynamo-caffeina-dev/airc-ui-kit'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'

export interface BubbleContent {
  name: string
  animate: string
  variant: 'default' | 'with-logo'
  href: string | null
  alt: string
  key?: string | number
  src: {
    id: number
    url: string
    caption: string | null
    alt_text: string | null
    name: string
    mime_type: string
  } & string
  content?: any
}

export interface BubbleClusterProps {
  content: BubbleContent[]
  preview?: boolean
  bubbleSize?: 'small' | 'medium' | 'big'
  isPartner?: boolean
}

const BubbleCluster = ({
  content,
  preview,
  bubbleSize = 'medium',
  isPartner = false,
}: BubbleClusterProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const initialItems = 9
  const hasPreview = Boolean(preview)
  const visibleContent = hasPreview
    ? isExpanded
      ? content
      : content.slice(0, initialItems)
    : content
  const hasMoreItems = hasPreview && content.length > initialItems
  const ChevronIcon = icons['chevronDown']
  const handleShowMore = () => setIsExpanded(!isExpanded)

  const containerVariants = {
    hidden: { height: 'auto', opacity: 0 },
    visible: { height: 'auto', opacity: 1, transition: { duration: 0.15 } },
  }

  const itemVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
  }

  return (
    <>
      <motion.div
        className={`bubble-cluster bubble-cluster--${bubbleSize}`}
        initial='hidden'
        animate='visible'
        variants={containerVariants}
      >
        <AnimatePresence>
          {visibleContent.map((content, index) => {
            const { name, key, variant, src, href, alt } = content
            const Element = href ? 'a' : 'div'
            return (
              <motion.div
                key={key || index}
                variants={itemVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                transition={{ delay: index * 0.02 }}
                className={`bubble-cluster__item bubble-cluster--${bubbleSize}__item bubble-cluster__item--${bubbleSize}`}
              >
                <Element
                  className={`bubble-cluster__anchor `}
                  {...(href && {
                    href,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                >
                  <Bubble
                    className={`bubble-cluster__bubble bubble-cluster--${bubbleSize}__bubble bubble-cluster__bubble--${bubbleSize}`}
                    variant={variant}
                    src={src}
                    alt={alt}
                  >
                    {name}
                  </Bubble>
                </Element>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </motion.div>
      {hasMoreItems && (
        <div className='bubble-cluster__show-more'>
          <Button
            className={`bubble-cluster__show-more__button`}
            variant='negative'
            onClick={handleShowMore}
          >
            {isExpanded
              ? 'Mostra meno'
              : `Mostra altri ${content.length - initialItems}`}
            <ChevronIcon
              className='bubble-cluster__show-more__icon'
              up={isExpanded}
            />
          </Button>
        </div>
      )}
    </>
  )
}

export default BubbleCluster
